import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { message } from 'antd';
import { BrowserRouter as Router } from 'react-router-dom';
import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { ThemeProvider } from 'styled-components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import './App.less';
import { Routes } from './app/Routes';
import EntityRegistry from './app/entity/EntityRegistry';
import { DashboardEntity } from './app/entity/dashboard/DashboardEntity';
import { ChartEntity } from './app/entity/chart/ChartEntity';
import { UserEntity } from './app/entity/user/User';
import { GroupEntity } from './app/entity/group/Group';
import { DatasetEntity } from './app/entity/dataset/DatasetEntity';
import { DataFlowEntity } from './app/entity/dataFlow/DataFlowEntity';
import { DataJobEntity } from './app/entity/dataJob/DataJobEntity';
import { TagEntity } from './app/entity/tag/Tag';
import { EntityRegistryContext } from './entityRegistryContext';
import { Theme } from './conf/theme/types';
import defaultThemeConfig from './conf/theme/theme_light.config.json';
import { checkAuthStatusFromServer, isLoggedInVar } from './app/auth/checkAuthStatus';
import { GlobalCfg } from './conf';
import { notifyAuthExpired, initializeAuthMonitoring } from './app/shared/authExpirationHandler';
import { GlossaryTermEntity } from './app/entity/glossaryTerm/GlossaryTermEntity';
import { MLFeatureEntity } from './app/entity/mlFeature/MLFeatureEntity';
import { MLPrimaryKeyEntity } from './app/entity/mlPrimaryKey/MLPrimaryKeyEntity';
import { MLFeatureTableEntity } from './app/entity/mlFeatureTable/MLFeatureTableEntity';
import { MLModelEntity } from './app/entity/mlModel/MLModelEntity';
import { MLModelGroupEntity } from './app/entity/mlModelGroup/MLModelGroupEntity';
import { DomainEntity } from './app/entity/domain/DomainEntity';
import { ContainerEntity } from './app/entity/container/ContainerEntity';
import GlossaryNodeEntity from './app/entity/glossaryNode/GlossaryNodeEntity';
import { DataPlatformEntity } from './app/entity/dataPlatform/DataPlatformEntity';
import { DataProductEntity } from './app/entity/dataProduct/DataProductEntity';
import { DataPlatformInstanceEntity } from './app/entity/dataPlatformInstance/DataPlatformInstanceEntity';
import { startLanguagePolling } from './utils/languagePolling';
import { RoleEntity } from './app/entity/Access/RoleEntity';
import AccessibilityHandler from './AccessibilityHandler';
import { LanguageProvider } from './i18n/LanguageContext';
import { setupLinkHandlerObservers } from './contextMenu';
import { loadLanguageAsync } from './i18n/i18n';
import utilService from './utils/util.service';
import { PageRoutes } from './conf/Global';
import { ERROR_WITH_STATUS_CODES_MAP } from './app/shared/constants';

/*
        Construct Apollo Client
    */
const httpLink = createHttpLink({ uri: '/api/v2/graphql' });

// Map of error operations and their corresponding status codes to bypass.
const byPassErrorWithStatusCodeMap = {
    getPrivaciTabProperties: ERROR_WITH_STATUS_CODES_MAP._404_NotFound,
    listPolicies: ERROR_WITH_STATUS_CODES_MAP._403_Forbidden,
    listIngestionSources: ERROR_WITH_STATUS_CODES_MAP._403_Forbidden,
    checkDownloadType: ERROR_WITH_STATUS_CODES_MAP._400_BadRequest,
};

/**
 * Checks if the error is bypassed based on the error code and status code.
 * @param error - The error object.
 * @param extensions - The extensions object.
 * @returns {boolean} - Returns true if the error is bypassed, otherwise false.
 */
const checkIfErrorIsByPassed = (error: any, extensions: any): boolean => {
    return byPassErrorWithStatusCodeMap[error?.operation.operationName] === extensions?.code;
};

/**
 * Handles GraphQL and network errors.
 * - For network errors with a 401 status code, logs out the user and redirects to the authentication page.
 * - For GraphQL errors, displays an error message unless the error is related to 'getPrivaciTabProperties' with a 404 status code.
 */
let isCheckingAuth = false;
const errorLink = onError((error) => {
    const { networkError, graphQLErrors } = error;
    if (networkError) {
        const serverError = networkError as ServerError;
        if (serverError.statusCode === 401) {
            // Check auth status from server and handle the result
            if (!isCheckingAuth) {
                isCheckingAuth = true;
                checkAuthStatusFromServer()
                    .then((response) => {
                        isCheckingAuth = false;
                        if (response.playSessionExpired === true) {
                            // this is for the case where the play session is expired,
                            // we need to re-authenticate the user and redirect them to the same page
                            const currentPath = window.location.pathname + window.location.search;
                            window.location.replace(
                                `${PageRoutes.AUTHENTICATE}?redirect_uri=${encodeURIComponent(currentPath)}`,
                            );
                            return;
                        }

                        // User is not authenticated, proceed with logout
                        // Notify parent window about token expiration
                        notifyAuthExpired(window.location.href);
                        localStorage.clear();

                        isLoggedInVar(false);
                        Cookies.remove(GlobalCfg.CLIENT_AUTH_COOKIE);
                    })
                    .catch((errorAuthStatus) => {
                        isCheckingAuth = false;
                        console.error('Error checking auth status:', errorAuthStatus);
                        // On error, proceed with logout as fallback
                        notifyAuthExpired(window.location.href);
                        localStorage.clear();
                        isLoggedInVar(false);
                        Cookies.remove(GlobalCfg.CLIENT_AUTH_COOKIE);
                    });
            }
        }
    }
    if (graphQLErrors && graphQLErrors.length) {
        const firstError = graphQLErrors[0];
        const { extensions } = firstError;
        const errorCode = extensions && (extensions.code as number);

        // Handle checkDownloadType before the partial-data guard, which would otherwise swallow it.
        if (
            errorCode === ERROR_WITH_STATUS_CODES_MAP._400_BadRequest &&
            error?.operation.operationName === 'checkDownloadType'
        ) {
            message.error(firstError.message);
            return;
        }

        // If the response contains partial data alongside errors (e.g. RBAC restricted access),
        // return early — the component will display whatever data was returned.
        const hasPartialData =
            error.response?.data && Object.values(error.response.data as Record<string, unknown>).length > 0;

        if (hasPartialData) return;

        if (!checkIfErrorIsByPassed(error, extensions)) {
            // Fallback in case the calling component does not handle.
            message.error(`${firstError.message}`, 3);
        }
    }
});

const withXSRFToken = setContext((_, { headers }) => {
    // get the xsrf token from local storage if it exists
    const xsrf = Cookies.get(GlobalCfg.XSRF_TOKEN);

    const xsrfParts = xsrf?.split('|') || [];
    const xsrfHeader = xsrfParts[0] ? atob(xsrfParts[0]) : '';
    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            'X-EDC-XSRF-TOKEN': xsrfHeader,
        },
    };
});

const authFlowLink = withXSRFToken.concat(errorLink);

const client = new ApolloClient({
    connectToDevTools: true,
    link: authFlowLink.concat(httpLink),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    dataset: {
                        merge: (oldObj, newObj) => {
                            return { ...oldObj, ...newObj };
                        },
                    },
                },
            },
        },
    }),
    credentials: 'include',
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'no-cache',
        },
        query: {
            fetchPolicy: 'no-cache',
        },
    },
});

const App: React.VFC = () => {
    const [dynamicThemeConfig, setDynamicThemeConfig] = useState<Theme>(defaultThemeConfig);
    const lang = utilService.getLanguage();

    // Initialize app on mount: authenticate and start language polling
    useEffect(() => {
        const authenticateOnLoad = async () => {
            try {
                const response = await fetch(PageRoutes.AUTHENTICATE, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                if (response.ok) {
                    console.log('Authentication API call successful');
                } else {
                    console.warn('Authentication API call failed:', response.status, response.statusText);
                }
            } catch (error) {
                console.error('Error during authentication API call:', error);
            }
        };

        // Start authentication and language polling
        authenticateOnLoad();

        // Start language polling
        const cleanup = startLanguagePolling();

        // Return cleanup function to stop polling
        return cleanup;
    }, []); // Empty dependency array ensures this runs only once on mount

    // Set language to Italian directly
    useEffect(() => {
        console.log(`Setting language to ${lang}`);
        loadLanguageAsync(lang)
            .then(() => {
                console.log(`Language successfully set to ${lang}`);
            })
            .catch((error) => {
                console.error(`Failed to set language to ${lang}:`, error);
            });
    }, [lang]);

    useEffect(() => {
        import(`./conf/theme/${process.env.REACT_APP_THEME_CONFIG}`).then((theme) => {
            setDynamicThemeConfig(theme);
        });
    }, []);

    const entityRegistry = useMemo(() => {
        const register = new EntityRegistry();
        register.register(new DatasetEntity());
        register.register(new DashboardEntity());
        register.register(new ChartEntity());
        register.register(new UserEntity());
        register.register(new GroupEntity());
        register.register(new TagEntity());
        register.register(new DataFlowEntity());
        register.register(new DataJobEntity());
        register.register(new GlossaryTermEntity());
        register.register(new MLFeatureEntity());
        register.register(new MLPrimaryKeyEntity());
        register.register(new MLFeatureTableEntity());
        register.register(new MLModelEntity());
        register.register(new MLModelGroupEntity());
        register.register(new DomainEntity());
        register.register(new ContainerEntity());
        register.register(new GlossaryNodeEntity());
        register.register(new RoleEntity());
        register.register(new DataPlatformEntity());
        register.register(new DataProductEntity());
        register.register(new DataPlatformInstanceEntity());
        return register;
    }, []);

    /**
     * Set up link handler observers and authentication monitoring when the component mounts
     */
    useEffect(() => {
        // Set up the link handler observers and get the cleanup function
        const cleanupObservers = setupLinkHandlerObservers();

        // Initialize authentication monitoring (token expiration and cookie removal)
        initializeAuthMonitoring();

        // Return the cleanup function to be called when the component unmounts
        return cleanupObservers;
    }, []);

    return (
        <HelmetProvider>
            <ThemeProvider theme={dynamicThemeConfig}>
                <LanguageProvider>
                    <Router>
                        <Helmet>
                            <title>{dynamicThemeConfig.content.title}</title>
                        </Helmet>
                        <EntityRegistryContext.Provider value={entityRegistry}>
                            <ApolloProvider client={client}>
                                <AccessibilityHandler>
                                    <Routes />
                                </AccessibilityHandler>
                            </ApolloProvider>
                        </EntityRegistryContext.Provider>
                    </Router>
                </LanguageProvider>
            </ThemeProvider>
        </HelmetProvider>
    );
};

export default App;
